import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '@/components/dashboard/PageHeader';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertCircle,
  RefreshCw,
  Copy,
  RotateCcw,
  Send,
  Eye,
  Inbox,
  KeyRound,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  loadAllCrewPortalContractorData,
  ensureCodesForCrew,
  regenerateCrewCode,
  setCrewActive,
  setCrewAccess,
  buildCrewPortalLink,
  sendCrewMessage,
  markCrewMessagesRead,
  flattenCrewInbox,
  formatUsd,
} from '@/lib/crewPortalData';
import { toast } from 'sonner';

const doCopy = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* noop */ }
  return false;
};

/* -------------------------------------------------------------------------
 * Roster + codes table
 * ----------------------------------------------------------------------- */
const RosterList = ({ crewMembers, roster, onAccess, onToggle, onRegen, onPreview, onMessage }) => {
  const rows = (crewMembers || []).map((c) => ({ crew: c, config: roster?.[c.id] || {} }));
  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="crew-portal-roster">
      <CardContent className="p-3 sm:p-4">
        <div className="hidden lg:grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
          <span className="col-span-3">Member</span>
          <span className="col-span-2">Code</span>
          <span className="col-span-3">Portal link</span>
          <span className="col-span-2">Access</span>
          <span className="col-span-1">Active</span>
          <span className="col-span-1 text-right">Actions</span>
        </div>
        {rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground" data-testid="crew-portal-roster-empty">
            {'No crew members yet. Add them in Settings \u2192 Crew rates.'}
          </div>
        ) : (
          rows.map(({ crew, config }) => (
            <div
              key={crew.id}
              className="grid grid-cols-12 gap-2 items-center px-3 py-3 border-b border-border"
              data-testid={`crew-portal-row-${crew.id}`}
            >
              <div className="col-span-12 lg:col-span-3 min-w-0">
                <div className="text-sm font-semibold text-foreground truncate">{crew.name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {crew.role || 'Crew'} {'\u00b7'} {formatUsd(crew.rate)}/h
                </div>
              </div>
              <div className="col-span-4 lg:col-span-2">
                <Badge
                  variant="outline"
                  className="font-mono tabular-nums text-sm tracking-[0.36em] border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 px-2.5 py-1"
                  data-testid={`crew-portal-code-${crew.id}`}
                >
                  {config.code || '\u2014'}
                </Badge>
              </div>
              <div className="col-span-8 lg:col-span-3 truncate">
                <span className="text-[11px] text-muted-foreground font-mono truncate block">
                  {config.code ? buildCrewPortalLink(config.code) : 'No code yet'}
                </span>
              </div>
              <div className="col-span-6 lg:col-span-2">
                <Select
                  value={config.access || 'full'}
                  onValueChange={(v) => onAccess(crew.id, v)}
                >
                  <SelectTrigger
                    className="h-8 bg-[hsl(var(--muted))] border-border text-xs"
                    data-testid={`crew-portal-access-${crew.id}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-border">
                    <SelectItem value="full">Full access</SelectItem>
                    <SelectItem value="clock">Clock only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-3 lg:col-span-1">
                <Switch
                  checked={!!config.active}
                  onCheckedChange={(v) => onToggle(crew.id, v)}
                  data-testid={`crew-portal-active-${crew.id}`}
                />
              </div>
              <div className="col-span-3 lg:col-span-1 flex items-center justify-end gap-1.5 flex-wrap">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={async () => {
                    const link = buildCrewPortalLink(config.code);
                    const ok = await doCopy(`${link} (Code: ${config.code})`);
                    if (ok) toast.success('Crew portal link + code copied');
                    else toast.error('Could not access clipboard');
                  }}
                  disabled={!config.code}
                  className="h-8 w-8 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
                  aria-label="Copy link + code"
                  data-testid={`crew-portal-copy-${crew.id}`}
                >
                  <Copy size={12} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onPreview(config.code)}
                  disabled={!config.code}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
                  aria-label="Preview portal"
                  data-testid={`crew-portal-preview-${crew.id}`}
                >
                  <Eye size={12} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onMessage(crew.id)}
                  className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
                  aria-label="Message"
                  data-testid={`crew-portal-message-${crew.id}`}
                >
                  <Send size={12} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onRegen(crew.id)}
                  className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/10"
                  aria-label="Regenerate code"
                  data-testid={`crew-portal-regen-${crew.id}`}
                >
                  <RotateCcw size={12} />
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

/* -------------------------------------------------------------------------
 * Inbox view
 * ----------------------------------------------------------------------- */
const InboxView = ({ inbox, activeId, onSelect, onSendReply, onMarkRead, sending }) => {
  const active = inbox.find((r) => r.crew_id === activeId) || null;
  const [reply, setReply] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    if (active.unread > 0) onMarkRead(active.crew_id);
    setReply('');
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, active?.messages?.length]);

  const handleSend = async () => {
    if (!active || !reply.trim()) return;
    await onSendReply(active.crew_id, reply.trim());
    setReply('');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="crew-portal-inbox">
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="flex items-center gap-2">
              <Inbox size={14} className="text-[hsl(var(--primary))]" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Crew threads</span>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {inbox.length} threads
            </span>
          </div>
          {inbox.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No crew messages yet.
            </div>
          ) : (
            <div className="space-y-1" data-testid="crew-portal-inbox-list">
              {inbox.map((row) => (
                <button
                  key={row.crew_id}
                  type="button"
                  onClick={() => onSelect(row.crew_id)}
                  data-testid={`crew-portal-inbox-row-${row.crew_id}`}
                  className={cn(
                    'w-full text-left rounded-md border px-3 py-2 transition-colors',
                    row.crew_id === activeId
                      ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5'
                      : 'border-border bg-[hsl(var(--muted))]/40 hover:border-[hsl(var(--primary))]/30'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-foreground truncate">
                      {row.crew_name}
                    </span>
                    {row.unread > 0 ? (
                      <Badge variant="outline" className="uppercase text-[9px] tracking-[0.16em] border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10">
                        {row.unread} new
                      </Badge>
                    ) : null}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {row.last_from === 'crew' ? 'Crew: ' : 'You: '}{row.last_body}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] lg:col-span-2">
        <CardContent className="p-3 sm:p-4">
          {!active ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              Select a thread to read and reply.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs font-semibold text-foreground">{active.crew_name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    Code {active.code} {'\u00b7'} {active.total} message{active.total === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
              <div
                ref={scrollRef}
                className="space-y-2 max-h-[420px] overflow-auto kc-scroll pr-1 mb-3"
                data-testid="crew-portal-thread"
              >
                {active.messages.map((m) => (
                  <div
                    key={m.id}
                    className={cn(
                      'rounded-md border px-3 py-2 max-w-[80%]',
                      m.from === 'contractor'
                        ? 'ml-auto border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10'
                        : 'border-border bg-[hsl(var(--muted))]/40'
                    )}
                  >
                    <div className="text-sm text-foreground whitespace-pre-wrap">{m.body}</div>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      {m.from === 'contractor' ? 'You' : active.crew_name} {'\u00b7'}{' '}
                      {m.sent_at ? new Date(m.sent_at).toLocaleString() : ''}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-end gap-2">
                <Textarea
                  rows={2}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Reply to crew..."
                  className="flex-1 bg-[hsl(var(--muted))] border-border text-foreground resize-none"
                  data-testid="crew-portal-reply-input"
                />
                <Button
                  type="button"
                  onClick={handleSend}
                  disabled={sending || !reply.trim()}
                  className="h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                  data-testid="crew-portal-reply-send"
                >
                  {sending ? <Loader2 size={12} className="mr-2 animate-spin" /> : <Send size={12} className="mr-2" />}
                  Send
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Page
 * ----------------------------------------------------------------------- */
export default function CrewPortalPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [crewMembers, setCrewMembers] = useState([]);
  const [roster, setRoster] = useState({});
  const [messages, setMessages] = useState({});

  const [activeInboxId, setActiveInboxId] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const data = await loadAllCrewPortalContractorData();
      setCrewMembers(data.crewMembers);
      const ensured = await ensureCodesForCrew(data.crewMembers, data.roster);
      setRoster(ensured);
      setMessages(data.messages);
    } catch (e) {
      setError(e?.message || t('crewPortalAdmin.loadError'));
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const inbox = useMemo(() => flattenCrewInbox(messages, roster), [messages, roster]);

  const handleRegen = async (crewId) => {
    try {
      const next = await regenerateCrewCode(crewId, roster);
      setRoster(next);
      toast.success('New code generated');
    } catch (e) {
      toast.error(e?.message || 'Failed to regenerate code.');
    }
  };

  const handleToggle = async (crewId, active) => {
    try {
      const next = await setCrewActive(crewId, active, roster);
      setRoster(next);
      toast.success(active ? 'Portal enabled' : 'Portal disabled');
    } catch (e) {
      toast.error(e?.message || 'Failed to update portal.');
    }
  };

  const handleAccess = async (crewId, access) => {
    try {
      const next = await setCrewAccess(crewId, access, roster);
      setRoster(next);
      toast.success(`Access set to ${access}`);
    } catch (e) {
      toast.error(e?.message || 'Failed to update access.');
    }
  };

  const handlePreview = (code) => {
    if (!code) return;
    window.open(`/portal/crew/${code}`, '_blank', 'noopener,noreferrer');
  };

  const handleStartMessage = async (crewId) => {
    setActiveInboxId(crewId);
    if (!messages[crewId]) {
      // create thread placeholder by sending a friendly first if empty
      // Skip auto-send; just open Inbox tab via state.
    }
  };

  const handleReply = async (crewId, body) => {
    setSending(true);
    try {
      const next = await sendCrewMessage({ crewId, from: 'contractor', body }, messages);
      setMessages(next);
      toast.success('Reply sent');
    } catch (e) {
      toast.error(e?.message || 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const handleMarkRead = async (crewId) => {
    try {
      const next = await markCrewMessagesRead(crewId, 'crew', messages);
      setMessages(next);
    } catch (e) { /* noop */ }
  };

  if (loading) {
    return (
      <div data-testid="crew-portal-page-loading">
        <PageHeader
          eyebrow={t('crewPortalAdmin.eyebrow')}
          title={t('crewPortalAdmin.title')}
          subtitle={t('crewPortalAdmin.subtitle')}
        />
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="mb-3 border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
            <CardContent className="p-4">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-10 w-full mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div data-testid="crew-portal-page">
      <PageHeader
        eyebrow={t('crewPortalAdmin.eyebrow')}
        title={t('crewPortalAdmin.title')}
        subtitle={t('crewPortalAdmin.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => load({ silent: true })}
            disabled={refreshing}
            className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="crew-portal-refresh"
          >
            <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t('crewPortalAdmin.refresh')}
          </Button>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="crew-portal-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      <Tabs defaultValue="roster" className="w-full" data-testid="crew-portal-tabs">
        <TabsList className="bg-[hsl(var(--muted))] border border-border mb-4 overflow-x-auto no-scrollbar">
          <TabsTrigger value="roster" className="text-xs uppercase tracking-[0.16em]">
            <KeyRound size={12} className="mr-1.5" />
            Roster + codes
          </TabsTrigger>
          <TabsTrigger value="inbox" className="text-xs uppercase tracking-[0.16em]">
            <Inbox size={12} className="mr-1.5" />
            Inbox {inbox.reduce((s, r) => s + (r.unread || 0), 0) > 0 ? `(${inbox.reduce((s, r) => s + (r.unread || 0), 0)})` : ''}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="roster">
          <RosterList
            crewMembers={crewMembers}
            roster={roster}
            onAccess={handleAccess}
            onToggle={handleToggle}
            onRegen={handleRegen}
            onPreview={handlePreview}
            onMessage={(id) => {
              setActiveInboxId(id);
              const tabsEl = document.querySelector('[data-testid="crew-portal-tabs"] [role="tablist"]');
              const inboxBtn = tabsEl?.querySelector('[value="inbox"]') || null;
              if (inboxBtn) inboxBtn.click();
              else handleStartMessage(id);
            }}
          />
        </TabsContent>

        <TabsContent value="inbox">
          <InboxView
            inbox={inbox}
            activeId={activeInboxId}
            onSelect={setActiveInboxId}
            onSendReply={handleReply}
            onMarkRead={handleMarkRead}
            sending={sending}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
